import { useParams } from 'react-router-dom'
import { useProtocolAnimal, useProcotol } from '@services/protocols/protocols.hook'
import {
  Box,
  Typography,
  FormControl,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Stack,
  Alert
} from '@mui/material'
import { usePersistedFormik } from '../hooks/usePersistedFormik'
import { AnimalHasAnimal, AnimalType, ANIMAL_TYPE } from '@services/protocols/protocols.consts'
import { getProtocols } from '@services/protocols/protocols.helpers'
import AutoSaveIndicator, { AutoSaveStyles } from './AutoSaveIndicator'

interface IAnimal {
  id?: number
  type: AnimalType
  quantity: number
  hasAnimals: AnimalHasAnimal
}

function ProtocolAnimals({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { protocol } = useProcotol(Number(id))
  const { currentProtocol } = getProtocols(protocol)

  const { protocolAnimal, createProtocolAnimal, refetch, updateProtocolAnimal } = useProtocolAnimal(
    Number(currentProtocol?.id)
  )

  const getInitialAnimals = () => {
    const animals = []

    //dogs
    const dogs = protocolAnimal.find((animal) => animal.animalType === AnimalType.dog)
    if (dogs) {
      animals.push({
        ...dogs,
        type: dogs.animalType,
        hasAnimals: dogs.quantity ? AnimalHasAnimal.yes : AnimalHasAnimal.no
      })
    } else {
      animals.push({
        type: AnimalType.dog,
        quantity: 0,
        hasAnimals: AnimalHasAnimal.no
      })
    }

    //cats
    const cats = protocolAnimal.find((animal) => animal.animalType === AnimalType.cat)
    if (cats) {
      animals.push({
        ...cats,
        type: cats.animalType,
        hasAnimals: cats.quantity ? AnimalHasAnimal.yes : AnimalHasAnimal.no
      })
    } else {
      animals.push({
        type: AnimalType.cat,
        quantity: 0,
        hasAnimals: AnimalHasAnimal.no
      })
    }
    //others
    const others = protocolAnimal.find((animal) => animal.animalType === AnimalType.other)
    if (others) {
      animals.push({
        ...others,
        type: others.animalType,
        hasAnimals: others.quantity ? AnimalHasAnimal.yes : AnimalHasAnimal.no
      })
    } else {
      animals.push({
        type: AnimalType.other,
        quantity: 0,
        hasAnimals: AnimalHasAnimal.no
      })
    }

    return animals
  }

  const animalsForm = usePersistedFormik(
    {
      validateOnMount: false,
      validateOnChange: false,
      enableReinitialize: true,
      initialValues: {
        animals: getInitialAnimals()
      },
      onSubmit: async (values: { animals: IAnimal[] }) => {
        values.animals.forEach(async (animal) => {
          if (animal.id) {
            await updateProtocolAnimal.mutateAsync({
              ...animal,
              animalType: animal.type,
              quantity: animal.hasAnimals === AnimalHasAnimal.no ? 0 : animal.quantity
            })
          } else {
            await createProtocolAnimal.mutateAsync({ ...animal, animalType: animal.type })
          }
        })

        refetch()
      }
    },
    {
      formType: 'animals',
      protocolId: Number(currentProtocol?.id || id),
      autoSaveInterval: 5000,
      showNotifications: false, // No spam notifications
      clearOnSubmit: true
    }
  )

  return (
    <Box>
      {/* Unsaved changes indicator */}
      {animalsForm.hasUnsavedChanges && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button size='small' onClick={animalsForm.restoreFromSaved} sx={{ mr: 1 }}>
              Odrzuć zmiany
            </Button>
          }
        >
          Formularz zawiera niezapisane zmiany
        </Alert>
      )}

      <form onSubmit={animalsForm.handleSubmit}>
        {animalsForm.values.animals?.map((animal: IAnimal, index: number) => (
          <Box key={index + animal.type} sx={{ my: 1 }}>
            <Typography variant='caption' fontWeight={700}>
              {ANIMAL_TYPE[animal.type as AnimalType]}
            </Typography>
            <FormControl fullWidth sx={{ my: 1 }}>
              <RadioGroup
                defaultValue='scale'
                value={animal.hasAnimals}
                onChange={({ target }) => {
                  const newAnimals = animalsForm.values.animals.map((item: IAnimal) => {
                    if (item.type === animal.type) {
                      return {
                        ...item,
                        hasAnimals: target.value
                      }
                    }
                    return item
                  })
                  animalsForm.setFieldValue(`animals`, newAnimals)
                }}
              >
                {Object.values(AnimalHasAnimal).map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {animal.hasAnimals === AnimalHasAnimal.yes && (
              <TextField
                fullWidth
                sx={{ my: 1 }}
                id='quantity'
                label='Ilość'
                variant='outlined'
                onChange={({ target }) => {
                  const newAnimals = animalsForm.values.animals.map((item: IAnimal) => {
                    if (item.type === animal.type) {
                      return {
                        ...item,
                        quantity: target.value
                      }
                    }
                    return item
                  })
                  animalsForm.setFieldValue(`animals`, newAnimals)
                }}
                value={animal.quantity}
                disabled={animalsForm.isSubmitting}
              />
            )}
          </Box>
        ))}
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mt: 2 }}>
          <AutoSaveIndicator
            isDirty={animalsForm.dirty && !animalsForm.hasUnsavedChanges}
            isAutoSaving={false}
            lastSavedTime={animalsForm.lastSaved}
          />
          <Button
            disabled={isReadOnly}
            type='submit'
            variant='contained'
            loading={animalsForm.isSubmitting}
          >
            Zapisz
          </Button>
        </Stack>
      </form>

      <style>{AutoSaveStyles}</style>
    </Box>
  )
}

export default ProtocolAnimals
